import React, { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "./Navbar";
import toast from "react-hot-toast";
import axiosInstance from "../config/axios";
import { DEFAULT_TAX_RATE } from "../constants/tax";
import { scannedAccounts } from "./ChartOfAccountsMenu";
import { OFFICE_EXPENSE_OPTIONS } from "../utils/officeExpenses";
import { roundTo2, roundTo2Str } from "../utils/numberUtils";

const todayYmd = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const emptyExpenseAmounts = () =>
  OFFICE_EXPENSE_OPTIONS.reduce((acc, opt) => {
    acc[opt.key] = "";
    return acc;
  }, {});

const formatDisplayDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString();
};

const formatCurrency = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const AgentExpenses = () => {
  const [agents, setAgents] = useState([]);
  const [recentRows, setRecentRows] = useState([]);
  const [expenseSummaryByAgent, setExpenseSummaryByAgent] = useState([]);
  const [balanceSummary, setBalanceSummary] = useState(null);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printAgentKey, setPrintAgentKey] = useState("");
  const [printFrom, setPrintFrom] = useState("");
  const [printTo, setPrintTo] = useState("");
  const [printing, setPrinting] = useState(false);

  const [form, setForm] = useState({
    invoiceDate: todayYmd(),
    postDate: todayYmd(),
    aeNumber: "AE1000",
    invoiceNumber: "6000",
    agentId: "",
    agentName: "",
    agentEmployeeNo: "",
    glAccount: "",
    taxRatePercent: String(Math.round(DEFAULT_TAX_RATE * 100)),
    expenseAmounts: emptyExpenseAmounts(),
  });

  const accruedByKey = useMemo(() => {
    const map = {};
    for (const opt of OFFICE_EXPENSE_OPTIONS) {
      const row = balanceSummary?.byOption?.find((b) => b.key === opt.key);
      map[opt.key] = row?.accrued || 0;
    }
    return map;
  }, [balanceSummary]);

  const balanceByKey = useMemo(() => {
    const map = {};
    for (const opt of OFFICE_EXPENSE_OPTIONS) {
      const row = balanceSummary?.byOption?.find((b) => b.key === opt.key);
      map[opt.key] = row?.balance || 0;
    }
    return map;
  }, [balanceSummary]);

  const glOptions = useMemo(
    () =>
      (scannedAccounts || []).map((a) => ({
        value: String(a.acct),
        label: `${a.acct} ${a.description}`,
        name: a.description,
      })),
    [],
  );

  const taxRate = useMemo(() => {
    const n = parseFloat(form.taxRatePercent);
    if (Number.isNaN(n)) return DEFAULT_TAX_RATE;
    return n > 1 ? n / 100 : n;
  }, [form.taxRatePercent]);

  const expenseLines = useMemo(() => {
    return OFFICE_EXPENSE_OPTIONS.map((opt) => {
      const amount = roundTo2(
        parseFloat(
          String(form.expenseAmounts[opt.key] || "").replace(/,/g, ""),
        ) || 0,
      );
      const tax = amount > 0 && !opt.taxExempt ? roundTo2(amount * taxRate) : 0;
      const total = amount > 0 ? roundTo2(amount + tax) : 0;
      return {
        key: opt.key,
        label: opt.label,
        amount,
        tax,
        total,
        taxExempt: !!opt.taxExempt,
      };
    }).filter((l) => l.amount > 0);
  }, [form.expenseAmounts, taxRate]);

  const totals = useMemo(() => {
    const amount = roundTo2(expenseLines.reduce((s, l) => s + l.amount, 0));
    const tax = roundTo2(expenseLines.reduce((s, l) => s + l.tax, 0));
    const total = roundTo2(amount + tax);
    return { amount, tax, total };
  }, [expenseLines]);

  const loadNextNumbers = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/agent-expenses/next-numbers");
      setForm((prev) => ({
        ...prev,
        aeNumber: res.data?.aeNumber || prev.aeNumber,
        invoiceNumber: res.data?.invoiceNumber || prev.invoiceNumber,
      }));
    } catch (e) {
      console.warn("Could not load next AE/invoice numbers:", e);
    }
  }, []);

  const loadAgents = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/agents");
      setAgents(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Error loading agents:", e);
      toast.error("Failed to load agents");
      setAgents([]);
    }
  }, []);

  const loadRecent = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/agent-expenses");
      const data = res.data;
      if (Array.isArray(data)) {
        setRecentRows(data);
        setExpenseSummaryByAgent([]);
      } else {
        setRecentRows(Array.isArray(data?.rows) ? data.rows : []);
        setExpenseSummaryByAgent(
          Array.isArray(data?.byExpenseByAgent) ? data.byExpenseByAgent : [],
        );
      }
    } catch (e) {
      console.error("Error loading agent expenses:", e);
      setRecentRows([]);
      setExpenseSummaryByAgent([]);
    }
  }, []);

  const expenseSummaryRows = useMemo(() => {
    const rows = [];
    for (const agentBlock of expenseSummaryByAgent || []) {
      const agentName = agentBlock.agentName || "Unknown agent";
      const agentId = agentBlock.agentId || "";
      for (const exp of agentBlock.byExpense || []) {
        if (Number(exp.total || 0) <= 0) continue;
        rows.push({
          agentId,
          agentName,
          key: exp.key,
          label: exp.label,
          total: Number(exp.total || 0),
          applied: Number(exp.applied || 0),
          balance: Number(exp.balance || 0),
          trades: Array.isArray(exp.trades) ? exp.trades : [],
        });
      }
    }
    return rows;
  }, [expenseSummaryByAgent]);

  const expenseSummaryGrandTotals = useMemo(() => {
    return {
      total: roundTo2(
        expenseSummaryRows.reduce((s, r) => s + Number(r.total || 0), 0),
      ),
      applied: roundTo2(
        expenseSummaryRows.reduce((s, r) => s + Number(r.applied || 0), 0),
      ),
      balance: roundTo2(
        expenseSummaryRows.reduce((s, r) => s + Number(r.balance || 0), 0),
      ),
    };
  }, [expenseSummaryRows]);

  const printAgentOptions = useMemo(() => {
    const fromForm = (agents || []).map((a) => {
      const name = `${a.firstName || ""} ${a.lastName || ""}`.trim();
      const emp =
        a.employeeNo != null && String(a.employeeNo).trim() !== ""
          ? String(a.employeeNo)
          : "";
      return {
        key: emp || String(a._id || ""),
        label: emp ? `${emp} - ${name}` : name || String(a._id || ""),
      };
    });
    const fromAe = [];
    const seen = new Set(fromForm.map((o) => o.key));
    for (const row of recentRows || []) {
      const id = String(row.agentId || "").trim();
      const name = String(row.agentName || "").trim();
      const key = id || name;
      if (!key || seen.has(key) || seen.has(name)) continue;
      seen.add(key);
      fromAe.push({ key, label: name || key });
    }
    return [...fromForm, ...fromAe].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
  }, [agents, recentRows]);

  const handlePrintExpenseReport = async () => {
    if (!printAgentKey) {
      toast.error("Please select an agent.");
      return;
    }
    if (printFrom && printTo && printFrom > printTo) {
      toast.error("From date must be on or before To date.");
      return;
    }
    setPrinting(true);
    try {
      const params = { agentKey: printAgentKey };
      if (printFrom) params.from = printFrom;
      if (printTo) params.to = printTo;
      const res = await axiosInstance.get("/agent-expenses/report", {
        params,
      });
      const data = res.data || {};
      const invoices = Array.isArray(data.invoices) ? data.invoices : [];
      const expenseColumns = Array.isArray(data.expenseColumns)
        ? data.expenseColumns
        : OFFICE_EXPENSE_OPTIONS.map((o) => ({ key: o.key, label: o.label }));
      const totals = data.totals || {
        total: 0,
        applied: 0,
        balance: 0,
        hst: 0,
      };
      const agentLabel =
        printAgentOptions.find((o) => o.key === printAgentKey)?.label ||
        data.agentName ||
        printAgentKey;
      const companyName = data.companyName || "ABC Realty Inc., Brokerage";
      const formatPeriodDate = (ymd) => {
        if (!ymd) return "";
        const [y, m, d] = String(ymd).split("-");
        if (!y || !m || !d) return ymd;
        return `${Number(m)}/${Number(d)}/${y}`;
      };
      const periodLabel =
        printFrom || printTo
          ? `${formatPeriodDate(printFrom) || "…"} – ${formatPeriodDate(printTo) || "…"} (by invoice date)`
          : "All dates (by invoice date)";
      const printed = new Date().toLocaleDateString();
      const money = (n) =>
        Number(n || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      const invDate = (v) => {
        if (!v) return "—";
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return "—";
        return d.toLocaleDateString();
      };

      const feeHeaders = expenseColumns
        .map((c) => `<th>${c.label}</th>`)
        .join("");
      const feeCells = (row) =>
        expenseColumns
          .map((c) => `<td class="num">${money(row[c.key])}</td>`)
          .join("");
      const feeFooter = expenseColumns
        .map((c) => `<td class="num">${money(totals[c.key])}</td>`)
        .join("");

      const bodyRows =
        invoices.length === 0
          ? `<tr><td colspan="${expenseColumns.length + 7}" style="text-align:center;padding:16px">No invoices for this agent/period.</td></tr>`
          : invoices
              .map(
                (row, idx) => `<tr class="${idx % 2 ? "alt" : ""}">
          <td>${row.aeNumber || "—"}</td>
          <td>${row.invoiceNumber || "—"}</td>
          <td>${invDate(row.invoiceDate)}</td>
          <td>${
            Number(row.applied || 0) > 0
              ? Array.isArray(row.appliedTrades) && row.appliedTrades.length
                ? row.appliedTrades.join(", ")
                : "—"
              : ""
          }</td>
          ${feeCells(row)}
          <td class="num">${money(row.hst)}</td>
          <td class="num">${money(row.total)}</td>
          <td class="num">${money(row.applied)}</td>
          <td class="num">${money(row.balance)}</td>
        </tr>`,
              )
              .join("");

      const printWindow = window.open("", "", "width=900,height=1100");
      if (!printWindow) {
        toast.error("Pop-up blocked. Allow pop-ups to print.");
        return;
      }
      printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Agent Expense Report - ${agentLabel}</title>
  <style>
    @page { size: portrait; margin: 10mm; }
    * { box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      color: #111;
      margin: 16px;
      font-size: 10px;
    }
    .header { text-align: center; margin-bottom: 10px; }
    .company { font-size: 22px; font-weight: 700; margin: 0 0 4px; }
    .title { font-size: 15px; font-weight: 700; margin: 0 0 8px; }
    .meta { font-size: 11px; line-height: 1.45; margin: 0; }
    .rule { border: 0; border-top: 1px solid #222; margin: 10px 0 14px; }
    .cards {
      display: flex;
      gap: 10px;
      margin-bottom: 14px;
    }
    .card {
      flex: 1;
      border: 1px solid #ccc;
      border-radius: 8px;
      padding: 10px 12px;
      background: #fff;
    }
    .card-label {
      font-size: 9px;
      letter-spacing: 0.04em;
      color: #555;
      font-weight: 600;
      margin-bottom: 4px;
    }
    .card-value {
      font-size: 16px;
      font-weight: 700;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    th, td {
      border: 1px solid #bbb;
      padding: 4px 3px;
      vertical-align: middle;
      word-wrap: break-word;
    }
    th {
      background: #f3f4f6;
      font-size: 8.5px;
      font-weight: 700;
      text-align: center;
    }
    td { font-size: 8.5px; text-align: left; }
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
    tr.alt td { background: #fafafa; }
    tfoot td {
      font-weight: 700;
      background: #f3f4f6;
      font-size: 8.5px;
    }
    .actions { margin-top: 14px; }
    .actions button {
      margin-right: 8px;
      padding: 6px 12px;
      cursor: pointer;
    }
    @media print {
      .actions { display: none !important; }
      body { margin: 0; }
      .card { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="company">${companyName}</div>
    <div class="title">Agent Expense Report</div>
    <p class="meta">
      Agent: ${agentLabel}<br/>
      Period: ${periodLabel}<br/>
      Printed: ${printed}
    </p>
  </div>
  <hr class="rule" />
  <div class="cards">
    <div class="card">
      <div class="card-label">TOTAL EXPENSES</div>
      <div class="card-value">$${money(totals.total)}</div>
    </div>
    <div class="card">
      <div class="card-label">APPLIED</div>
      <div class="card-value">$${money(totals.applied)}</div>
    </div>
    <div class="card">
      <div class="card-label">BALANCE</div>
      <div class="card-value">$${money(totals.balance)}</div>
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <th>AE #</th>
        <th>Invoice #</th>
        <th>Invoice Date</th>
        <th>Applied Trades</th>
        ${feeHeaders}
        <th>HST</th>
        <th>Total</th>
        <th>Applied</th>
        <th>Balance</th>
      </tr>
    </thead>
    <tbody>
      ${bodyRows}
    </tbody>
    <tfoot>
      <tr>
        <td colspan="4">Totals (${invoices.length} invoice${invoices.length === 1 ? "" : "s"})</td>
        ${feeFooter}
        <td class="num">${money(totals.hst)}</td>
        <td class="num">${money(totals.total)}</td>
        <td class="num">${money(totals.applied)}</td>
        <td class="num">${money(totals.balance)}</td>
      </tr>
    </tfoot>
  </table>
  <div class="actions">
    <button onclick="window.print()">Print</button>
    <button onclick="window.close()">Close</button>
  </div>
</body>
</html>`);
      printWindow.document.close();
      printWindow.focus();
    } catch (e) {
      console.error("Error printing agent expense report:", e);
      toast.error(
        e?.response?.data?.message || "Failed to load expense report",
      );
    } finally {
      setPrinting(false);
    }
  };

  const loadBalancesForAgent = useCallback(async (agentKey, applySuggested) => {
    if (!agentKey) {
      setBalanceSummary(null);
      return;
    }
    setLoadingBalances(true);
    try {
      const res = await axiosInstance.get(
        `/agent-expenses/balances/${encodeURIComponent(agentKey)}`,
      );
      setBalanceSummary(res.data || null);
      if (applySuggested && res.data?.suggestedAmounts) {
        const suggested = res.data.suggestedAmounts;
        setForm((prev) => ({
          ...prev,
          expenseAmounts: {
            ...emptyExpenseAmounts(),
            ...Object.fromEntries(
              Object.entries(suggested).map(([k, v]) => [
                k,
                v == null || v === "" ? "" : String(v),
              ]),
            ),
          },
        }));
      }
    } catch (e) {
      console.error("Error loading agent expense balances:", e);
      toast.error(
        e.response?.data?.message ||
          "Failed to load office expense amounts for this agent",
      );
      setBalanceSummary(null);
    } finally {
      setLoadingBalances(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadAgents(), loadNextNumbers(), loadRecent()]).finally(() =>
      setLoading(false),
    );
  }, [loadAgents, loadNextNumbers, loadRecent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "agentId") {
      const selected = agents.find((a) => String(a._id) === String(value));
      const agentName = selected
        ? `${selected.firstName || ""} ${selected.lastName || ""}`.trim()
        : "";
      const agentEmployeeNo =
        selected?.employeeNo != null ? String(selected.employeeNo) : "";
      setForm((prev) => ({
        ...prev,
        agentId: value,
        agentName,
        agentEmployeeNo,
        expenseAmounts: emptyExpenseAmounts(),
      }));
      const key = agentEmployeeNo || value;
      if (key) loadBalancesForAgent(key, false);
      else setBalanceSummary(null);
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleExpenseAmountChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      expenseAmounts: {
        ...prev.expenseAmounts,
        [key]: value,
      },
    }));
  };

  const resetForm = async ({ keepAgent = false } = {}) => {
    const kept = keepAgent
      ? {
          agentId: form.agentId,
          agentName: form.agentName,
          agentEmployeeNo: form.agentEmployeeNo,
        }
      : {
          agentId: "",
          agentName: "",
          agentEmployeeNo: "",
        };

    setForm({
      invoiceDate: todayYmd(),
      postDate: todayYmd(),
      aeNumber: "AE1000",
      invoiceNumber: "6000",
      ...kept,
      glAccount: "",
      taxRatePercent: String(Math.round(DEFAULT_TAX_RATE * 100)),
      expenseAmounts: emptyExpenseAmounts(),
    });
    if (!keepAgent) setBalanceSummary(null);
    await loadNextNumbers();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.agentId || !form.agentName) {
      toast.error("Please select an agent");
      return;
    }
    if (!form.glAccount) {
      toast.error("Please select a G/L account");
      return;
    }
    if (totals.total <= 0) {
      toast.error("Enter at least one office expense amount");
      return;
    }

    const gl = glOptions.find((o) => o.value === form.glAccount);
    const agentKey = form.agentEmployeeNo || form.agentId;

    setSaving(true);
    try {
      const res = await axiosInstance.post("/agent-expenses", {
        invoiceDate: form.invoiceDate,
        postDate: form.postDate,
        agentId: agentKey,
        agentName: form.agentName,
        glAccount: form.glAccount,
        glAccountName: gl?.name || "",
        taxRate,
        officeExpenses: expenseLines,
        amount: totals.amount,
        tax: totals.tax,
        total: totals.total,
      });

      const posting = res.data?.ledgerPosting;
      if (posting) {
        const parts = [];
        if (Number(posting.debit12100) > 0) {
          parts.push(`Debit 12100 $${Number(posting.debit12100).toFixed(2)}`);
        }
        if (Number(posting.creditGl) > 0) {
          parts.push(
            `Credit ${posting.creditAccount} $${Number(posting.creditGl).toFixed(2)}`,
          );
        }
        if (Number(posting.credit23000) > 0) {
          parts.push(
            `Credit 23000 $${Number(posting.credit23000).toFixed(2)}`,
          );
        }
        if (Number(posting.debit23000) > 0) {
          parts.push(`Debit 23000 $${Number(posting.debit23000).toFixed(2)}`);
        }
        toast.success(`Posted ${res.data.aeNumber}: ${parts.join("; ")}`);
      } else {
        toast.success(
          `Agent expense ${res.data?.aeNumber || ""} posted successfully`,
        );
      }
      // Keep agent selected so balance tables stay visible and refresh
      await resetForm({ keepAgent: true });
      await loadRecent();
      if (agentKey) await loadBalancesForAgent(agentKey, false);
    } catch (err) {
      console.error("Error posting agent expense:", err);
      toast.error(
        err.response?.data?.message || "Failed to post agent expense",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Navbar />
      <div className="flex flex-1 overflow-hidden">
        <main className="flex-1 overflow-y-auto p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            Agent Expenses
          </h1>

          {loading ? (
            <p className="text-gray-500">Loading…</p>
          ) : (
            <>
              <form
                onSubmit={handleSubmit}
                className="bg-white border border-gray-200 rounded-lg p-6 space-y-4 max-w-2xl shadow-sm"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice date
                  </label>
                  <input
                    type="date"
                    name="invoiceDate"
                    value={form.invoiceDate}
                    onChange={handleChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Defaults to today — change to override.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    AE Number
                  </label>
                  <input
                    type="text"
                    name="aeNumber"
                    value={form.aeNumber}
                    readOnly
                    className="block w-full rounded-md border-gray-300 shadow-sm bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Agent name
                  </label>
                  <select
                    name="agentId"
                    value={form.agentId}
                    onChange={handleChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                    required
                  >
                    <option value="">Select agent…</option>
                    {agents.map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.firstName} {a.lastName}
                        {a.employeeNo != null ? ` (#${a.employeeNo})` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice #
                  </label>
                  <input
                    type="text"
                    name="invoiceNumber"
                    value={form.invoiceNumber}
                    readOnly
                    className="block w-full rounded-md border-gray-300 shadow-sm bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    G/L Account
                  </label>
                  <select
                    name="glAccount"
                    value={form.glAccount}
                    onChange={handleChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                    required
                  >
                    <option value="">Select G/L Account</option>
                    {glOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Post date
                  </label>
                  <input
                    type="date"
                    name="postDate"
                    value={form.postDate}
                    onChange={handleChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Defaults to today — change to override.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-800">
                    Office Expenses
                  </h3>
                  <p className="text-xs text-gray-500">
                    Enter amounts to post a new Agent Expense invoice. Existing
                    AE invoices for this agent appear in the balances tables
                    below. On Agent Commission Info, selecting the agent fills
                    Office Expenses with the remaining balance (you can
                    override).
                  </p>
                  {loadingBalances && (
                    <p className="text-xs text-blue-700">Loading balances…</p>
                  )}
                  {form.agentId &&
                    balanceSummary &&
                    !(balanceSummary.byOption || []).some(
                      (r) => Number(r.accrued) > 0,
                    ) &&
                    !loadingBalances && (
                      <p className="text-xs text-amber-700">
                        No Agent Expense invoices found for this agent yet. Post
                        an expense below, then those amounts will appear under
                        Office Expenses when you select the agent on a trade.
                      </p>
                    )}
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-xs font-medium text-gray-600">
                      Tax rate (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name="taxRatePercent"
                      value={form.taxRatePercent}
                      onChange={handleChange}
                      className="w-24 rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-700 focus:ring-blue-700"
                    />
                    <span className="text-xs text-gray-500">
                      Applied to each expense (default{" "}
                      {Math.round(DEFAULT_TAX_RATE * 100)}%)
                    </span>
                  </div>

                  {OFFICE_EXPENSE_OPTIONS.map((opt) => {
                    const raw = form.expenseAmounts[opt.key] || "";
                    const amt = parseFloat(String(raw).replace(/,/g, "")) || 0;
                    const lineTax =
                      amt > 0 && !opt.taxExempt ? roundTo2(amt * taxRate) : 0;
                    const lineTotal = amt > 0 ? roundTo2(amt + lineTax) : 0;
                    const accrued = accruedByKey[opt.key] || 0;
                    const bal = balanceByKey[opt.key] || 0;
                    return (
                      <div
                        key={opt.key}
                        className="flex flex-wrap items-center gap-3 text-sm"
                      >
                        <span className="min-w-[11rem] font-medium text-gray-700">
                          {opt.label}
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={raw}
                          onChange={(e) =>
                            handleExpenseAmountChange(opt.key, e.target.value)
                          }
                          placeholder="Amount"
                          className="w-32 rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-700 focus:ring-blue-700"
                        />
                        {form.agentId && (
                          <span className="text-xs text-gray-500 tabular-nums">
                            Posted ${roundTo2Str(accrued)} · Remaining $
                            {roundTo2Str(bal)}
                          </span>
                        )}
                        {amt > 0 &&
                          (opt.taxExempt ? (
                            <span className="text-xs text-gray-600 tabular-nums">
                              (no tax) = ${roundTo2Str(lineTotal)}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-600 tabular-nums">
                              + tax ${roundTo2Str(lineTax)} = $
                              {roundTo2Str(lineTotal)}
                            </span>
                          ))}
                      </div>
                    );
                  })}

                  <div className="pt-2 border-t border-gray-200 text-sm text-gray-800 space-y-1">
                    <div className="flex justify-between">
                      <span>Amount</span>
                      <span className="tabular-nums">
                        ${roundTo2Str(totals.amount)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>HST</span>
                      <span className="tabular-nums">
                        ${roundTo2Str(totals.tax)}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold">
                      <span>Total</span>
                      <span className="tabular-nums">
                        ${roundTo2Str(totals.total)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 pt-1">
                      Other fees: Debit 12100 (total) / Credit selected G/L
                      (amount) / Credit 23000 (HST). Commission deduction:
                      Debit 12100 (amount) / Debit 23000 (HST) / Credit
                      selected G/L (total).
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    disabled={saving}
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-2 bg-blue-900 text-white rounded-md font-semibold hover:bg-blue-950 disabled:opacity-60"
                  >
                    {saving ? "Posting…" : "Post Expense"}
                  </button>
                </div>
              </form>

              {form.agentId && balanceSummary && (
                <div className="mt-8 bg-white border border-gray-200 rounded-lg p-4 shadow-sm max-w-4xl">
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">
                    Office Expense Balances
                  </h2>
                  <p className="text-xs text-gray-500 mb-3">
                    Posted = Agent Expense invoices. Applied = deducted on
                    trades after agent commission EFT. Balance = Posted −
                    Applied (also fills Office Expenses when you select the
                    agent on Agent Commission Info).
                  </p>
                  <div className="overflow-x-auto mb-6">
                    <table className="min-w-full text-sm border border-gray-300">
                      <thead>
                        <tr className="bg-gray-100 text-left">
                          <th className="px-3 py-2 border-b border-r">
                            Expense
                          </th>
                          <th className="px-3 py-2 border-b border-r text-right">
                            Posted (AE)
                          </th>
                          <th className="px-3 py-2 border-b border-r text-right">
                            Applied on trades
                          </th>
                          <th className="px-3 py-2 border-b text-right">
                            Balance
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(balanceSummary.byOption || []).map((row) => (
                          <tr key={row.key}>
                            <td className="px-3 py-2 border-b border-r">
                              {row.label}
                            </td>
                            <td className="px-3 py-2 border-b border-r text-right tabular-nums">
                              {formatCurrency(row.accrued)}
                            </td>
                            <td className="px-3 py-2 border-b border-r text-right tabular-nums">
                              {formatCurrency(row.collected)}
                            </td>
                            <td className="px-3 py-2 border-b text-right tabular-nums font-medium">
                              {formatCurrency(row.balance)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <h3 className="text-sm font-semibold text-gray-800 mb-2">
                    Agent Expense invoices
                  </h3>
                  <div className="overflow-x-auto mb-6">
                    <table className="min-w-full text-sm border border-gray-300">
                      <thead>
                        <tr className="bg-gray-100 text-left">
                          <th className="px-3 py-2 border-b border-r">AE #</th>
                          <th className="px-3 py-2 border-b border-r">
                            Invoice #
                          </th>
                          <th className="px-3 py-2 border-b border-r">
                            Expense
                          </th>
                          <th className="px-3 py-2 border-b text-right">
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(balanceSummary.aeRows || []).length === 0 ? (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-3 py-4 text-center text-gray-500"
                            >
                              No Agent Expense line items for this agent.
                            </td>
                          </tr>
                        ) : (
                          balanceSummary.aeRows.map((row, idx) => (
                            <tr
                              key={`${row.aeNumber}-${row.expenseKey}-${idx}`}
                            >
                              <td className="px-3 py-2 border-b border-r">
                                {row.aeNumber}
                              </td>
                              <td className="px-3 py-2 border-b border-r">
                                {row.invoiceNumber}
                              </td>
                              <td className="px-3 py-2 border-b border-r">
                                {row.expenseLabel}
                              </td>
                              <td className="px-3 py-2 border-b text-right tabular-nums">
                                {formatCurrency(row.amount)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <h3 className="text-sm font-semibold text-gray-800 mb-2">
                    Applied on trades (after agent EFT)
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm border border-gray-300">
                      <thead>
                        <tr className="bg-gray-100 text-left">
                          <th className="px-3 py-2 border-b border-r">
                            Trade #
                          </th>
                          <th className="px-3 py-2 border-b border-r">
                            Address
                          </th>
                          <th className="px-3 py-2 border-b border-r">EFT #</th>
                          <th className="px-3 py-2 border-b border-r">
                            Expense
                          </th>
                          <th className="px-3 py-2 border-b text-right">
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(balanceSummary.tradeRows || []).length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-3 py-4 text-center text-gray-500"
                            >
                              No office expenses found on trades with an agent
                              commission EFT for this agent.
                            </td>
                          </tr>
                        ) : (
                          balanceSummary.tradeRows.map((row, idx) => (
                            <tr
                              key={`${row.tradeId}-${row.expenseKey}-${row.eftNumber}-${idx}`}
                            >
                              <td className="px-3 py-2 border-b border-r">
                                {row.tradeNumber}
                              </td>
                              <td className="px-3 py-2 border-b border-r">
                                {row.address || "—"}
                              </td>
                              <td className="px-3 py-2 border-b border-r">
                                {row.eftNumber ||
                                  (row.eftIssued ? "—" : "Pending EFT")}
                              </td>
                              <td className="px-3 py-2 border-b border-r">
                                {row.expenseLabel}
                              </td>
                              <td className="px-3 py-2 border-b text-right tabular-nums">
                                {formatCurrency(row.amount)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mt-8 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  Recent Agent Expenses
                </h2>
                <p className="text-xs text-gray-500 mb-3">
                  Applied = office expenses recovered on trades after agent
                  commission EFT. Balance = Total − Applied (remaining).
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border border-gray-300">
                    <thead>
                      <tr className="bg-gray-100 text-left">
                        <th className="px-3 py-2 border-b border-r">AE #</th>
                        <th className="px-3 py-2 border-b border-r">
                          Invoice #
                        </th>
                        <th className="px-3 py-2 border-b border-r">Agent</th>
                        <th className="px-3 py-2 border-b border-r">G/L</th>
                        <th className="px-3 py-2 border-b border-r">
                          Invoice Date
                        </th>
                        <th className="px-3 py-2 border-b border-r">
                          Post Date
                        </th>
                        <th className="px-3 py-2 border-b border-r text-right">
                          Total
                        </th>
                        <th className="px-3 py-2 border-b border-r text-right">
                          Applied
                        </th>
                        <th className="px-3 py-2 border-b text-right">
                          Balance
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={9}
                            className="px-3 py-4 text-center text-gray-500"
                          >
                            No agent expenses posted yet.
                          </td>
                        </tr>
                      ) : (
                        recentRows.map((row) => (
                          <tr key={row._id}>
                            <td className="px-3 py-2 border-b border-r">
                              {row.aeNumber}
                            </td>
                            <td className="px-3 py-2 border-b border-r">
                              {row.invoiceNumber}
                            </td>
                            <td className="px-3 py-2 border-b border-r">
                              {row.agentName}
                            </td>
                            <td className="px-3 py-2 border-b border-r">
                              {row.glAccount}
                              {row.glAccountName ? ` ${row.glAccountName}` : ""}
                            </td>
                            <td className="px-3 py-2 border-b border-r">
                              {formatDisplayDate(row.invoiceDate)}
                            </td>
                            <td className="px-3 py-2 border-b border-r">
                              {formatDisplayDate(row.postDate)}
                            </td>
                            <td className="px-3 py-2 border-b border-r text-right tabular-nums">
                              {formatCurrency(row.total)}
                            </td>
                            <td className="px-3 py-2 border-b border-r text-right tabular-nums">
                              {formatCurrency(row.applied)}
                            </td>
                            <td className="px-3 py-2 border-b text-right tabular-nums font-medium">
                              {formatCurrency(
                                row.balance != null
                                  ? row.balance
                                  : Math.max(
                                      0,
                                      Number(row.total || 0) -
                                        Number(row.applied || 0),
                                    ),
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <h3 className="text-base font-semibold text-gray-900 mt-6 mb-1">
                  Expense Totals by Type
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Total / Applied / Balance for each office expense, by agent.
                  Applied updates after Agent Commission EFT (Commission Trust).
                  Trades lists the trade number(s) where that Applied amount was
                  recovered.
                </p>

                <div className="mb-4 p-3 border border-gray-200 rounded-md bg-gray-50 max-w-4xl">
                  <div className="text-sm font-medium text-gray-800 mb-2">
                    Print expense report
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="min-w-[200px]">
                      <label className="block text-xs text-gray-600 mb-1">
                        Agent
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white"
                        value={printAgentKey}
                        onChange={(e) => setPrintAgentKey(e.target.value)}
                      >
                        <option value="">Select agent…</option>
                        {printAgentOptions.map((opt) => (
                          <option key={opt.key} value={opt.key}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        From (invoice date)
                      </label>
                      <input
                        type="date"
                        className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white"
                        value={printFrom}
                        onChange={(e) => setPrintFrom(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        To (invoice date)
                      </label>
                      <input
                        type="date"
                        className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white"
                        value={printTo}
                        onChange={(e) => setPrintTo(e.target.value)}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handlePrintExpenseReport}
                      disabled={printing}
                      className="px-4 py-1.5 bg-blue-900 text-white text-sm rounded-md font-medium hover:bg-blue-950 disabled:opacity-60"
                    >
                      {printing ? "Loading…" : "Print Report"}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Leave dates empty to include all invoices for the selected
                    agent.
                  </p>
                </div>

                <div className="overflow-x-auto max-w-5xl">
                  <table className="min-w-full text-sm border border-gray-300">
                    <thead>
                      <tr className="bg-gray-100 text-left">
                        <th className="px-3 py-2 border-b border-r">Agent</th>
                        <th className="px-3 py-2 border-b border-r">Expense</th>
                        <th className="px-3 py-2 border-b border-r text-right">
                          Total
                        </th>
                        <th className="px-3 py-2 border-b border-r text-right">
                          Applied
                        </th>
                        <th className="px-3 py-2 border-b border-r text-right">
                          Balance
                        </th>
                        <th className="px-3 py-2 border-b">Trades</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenseSummaryRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-3 py-4 text-center text-gray-500"
                          >
                            No expense line totals yet.
                          </td>
                        </tr>
                      ) : (
                        <>
                          {expenseSummaryRows.map((row) => (
                            <tr
                              key={`${row.agentId}-${row.agentName}-${row.key}`}
                            >
                              <td className="px-3 py-2 border-b border-r">
                                {row.agentName}
                              </td>
                              <td className="px-3 py-2 border-b border-r">
                                {row.label}
                              </td>
                              <td className="px-3 py-2 border-b border-r text-right tabular-nums">
                                {formatCurrency(row.total)}
                              </td>
                              <td className="px-3 py-2 border-b border-r text-right tabular-nums">
                                {formatCurrency(row.applied)}
                              </td>
                              <td className="px-3 py-2 border-b border-r text-right tabular-nums font-medium">
                                {formatCurrency(row.balance)}
                              </td>
                              <td className="px-3 py-2 border-b text-sm text-gray-800">
                                {Number(row.applied || 0) > 0
                                  ? row.trades?.length
                                    ? row.trades.join(", ")
                                    : "—"
                                  : ""}
                              </td>
                            </tr>
                          ))}
                          <tr className="bg-gray-50 font-semibold">
                            <td className="px-3 py-2 border-r" colSpan={2}>
                              Total
                            </td>
                            <td className="px-3 py-2 border-r text-right tabular-nums">
                              {formatCurrency(expenseSummaryGrandTotals.total)}
                            </td>
                            <td className="px-3 py-2 border-r text-right tabular-nums">
                              {formatCurrency(
                                expenseSummaryGrandTotals.applied,
                              )}
                            </td>
                            <td className="px-3 py-2 border-r text-right tabular-nums">
                              {formatCurrency(
                                expenseSummaryGrandTotals.balance,
                              )}
                            </td>
                            <td className="px-3 py-2" />
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default AgentExpenses;
